/**
 * `useCan` and `<Can>` — the permission-aware rendering primitives.
 *
 * ===========================================================================
 * THIS IS FOR RENDERING ONLY. IT IS NOT A SECURITY CONTROL.
 * ===========================================================================
 * The server re-checks every capability and every resource scope on execution
 * (`kernel/authorization/policy.py`). Hiding a button protects nothing: anyone with
 * developer tools can call the endpoint, and the endpoint will refuse them. What this
 * buys is a kinder interface — a user is not shown an action they cannot take.
 *
 * The failure mode to watch for in review is the inverse reasoning: "the UI hides
 * this, so the endpoint does not need to check it". If you ever catch yourself
 * thinking that, the endpoint is the bug and this file is not the fix.
 * ===========================================================================
 *
 * WHICH CHECK TO USE
 *
 *   You have a RECORD in hand      -> `_actions` on its payload, via `allows()` or
 *                                     `<Can actions={record._actions} action="edit">`.
 *                                     This is the accurate one: it is computed by the
 *                                     kernel against THIS record, including scope.
 *
 *   You are drawing NAVIGATION,     -> `useCan('case', 'create')`. A capability check
 *   a toolbar, or a "new" button       with no record to scope against.
 *
 * A capability check answers "may this person transition cases at all", never "may
 * this person transition case 123". The frontend cannot answer the second — scope
 * resolution needs case-team membership and per-matter grants, which live server-side
 * and are deliberately never cached beyond a unit of work.
 */

import { allows, type PermissionAction, type PermissionModule, type PermissionScope, type ResourceActions } from '@legalcrm/api';
import type { ReactNode } from 'react';

import { useAuth } from './AuthProvider';

/** Does the signed-in user hold `module.action` at ANY scope? */
export function useCan(
  module: PermissionModule | string,
  action: PermissionAction | string,
): boolean {
  return useAuth().permissions.has(module, action);
}

/**
 * Does the user hold `module.action` at `scope` or wider?
 *
 * For chrome that only makes sense at a certain breadth — a firm-wide filter, a
 * department selector. Not for deciding access to a record.
 */
export function useCanAtLeast(
  module: PermissionModule | string,
  action: PermissionAction | string,
  scope: PermissionScope,
): boolean {
  return useAuth().permissions.hasAtLeast(module, action, scope);
}

/** True if any of the capabilities is held. For "should this nav section exist". */
export function useCanAny(capabilities: Array<[string, string]>): boolean {
  return useAuth().permissions.hasAny(capabilities);
}

export interface CanProps {
  /** Capability form: `<Can module="case" action="create">`. */
  module?: PermissionModule | string;
  action: PermissionAction | string;
  /**
   * Record form: `<Can actions={caseRecord._actions} action="edit">`.
   * When supplied this WINS over the capability check, because it is the accurate
   * answer for this specific record.
   */
  actions?: ResourceActions;
  scope?: PermissionScope;
  /** Rendered when the check fails. Usually nothing; occasionally a disabled state. */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Render `children` only when the check passes.
 *
 * Deliberately renders NOTHING by default when it fails, rather than a disabled
 * control or an explanation. "You do not have permission to close this matter" on a
 * screen belonging to a colleague's caseload tells the reader that the matter exists
 * and what could be done to it — which is exactly what the 404-for-out-of-scope
 * mapping in the API is designed to avoid saying. Where a disabled control genuinely
 * helps (a blocked transition, which the server has already explained), pass the
 * server's reason through `fallback`.
 */
export function Can({ module, action, actions, scope, fallback = null, children }: CanProps) {
  const permissions = useAuth().permissions;

  let permitted: boolean;
  if (actions) {
    permitted = allows(actions, action);
  } else if (!module) {
    throw new Error('<Can> needs either a `module` or an `actions` block.');
  } else if (scope) {
    permitted = permissions.hasAtLeast(module, action, scope);
  } else {
    permitted = permissions.has(module, action);
  }

  return <>{permitted ? children : fallback}</>;
}
